import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { IncidentAction, incidentActionsQuery } from '../api/securitee'

/** Interogarea unei benzi: a incidentului (Securitee) sau a tichetului (fila XDR) - aceeasi forma. */
type ActionsQuery = ReturnType<typeof incidentActionsQuery>

const PENDING = new Set(['waiting', 'running'])

/**
 * "Response actions": ce s-a facut pe aparatul incidentului si daca chiar s-a
 * aplicat - trimisa (cine, cand) -> preluata de agent -> aplicata / esuata /
 * expirata, cu raspunsul agentului. Cat timp o actiune e pe drum, se reciteste
 * la 5 secunde.
 *
 * Ultima actiune sta mereu desfacuta, sus, afara din istoric; cele dinaintea ei
 * stau sub "Earlier actions (N)", stranse, desfacute la nevoie - altfel lungeau
 * pagina. Istoricul se deschide singur cat timp o actiune veche e inca pe drum.
 */
export function ResponseActionsList({ query, onUnblock }: { query: ActionsQuery; onUnblock?: (a: IncidentAction) => Promise<void> }) {
  const q = useQuery({
    ...query,
    refetchInterval: (state) => (state.state.data?.actions.some((a) => PENDING.has(a.result)) ? 5000 : false),
  })

  const actions = q.data?.actions ?? []
  const last = actions[0]
  const earlier = actions.slice(1)
  // null = inca nealeasa de om: stransa, dar desfacuta cat timp o actiune veche e pe drum
  const [chosen, setChosen] = useState<boolean | null>(null)
  const open = chosen ?? earlier.some((a) => PENDING.has(a.result))
  // "Unblock now" numai unde pagina il da (incidentul) si omul are dreptul Block IP
  const unblock = q.data?.can_unblock ? onUnblock : undefined

  return (
    <div className="incident-actions" style={{ marginTop: 12 }}>
      <h5 style={{ fontWeight: 600 }}>
        Response actions <span className="font-grey-cascade" style={{ fontWeight: 400 }}>(UTC)</span>
      </h5>
      {q.isError ? <p className="color-red">{q.error instanceof Error ? q.error.message : 'The response actions could not be read.'}</p> : null}
      {q.data && actions.length === 0 ? <p className="font-grey-cascade">No response actions yet.</p> : null}
      {last ? <ActionCard a={last} onUnblock={unblock} /> : null}
      {earlier.length > 0 ? (
        <>
          <button
            type="button"
            className="btn btn-link incident-actions-toggle"
            style={{ padding: 0, fontWeight: 600, color: 'inherit', textDecoration: 'none' }}
            aria-expanded={open}
            onClick={() => setChosen(!open)}
          >
            <i className={`fa fa-chevron-${open ? 'down' : 'right'}`} aria-hidden="true" style={{ width: 14 }}></i> Earlier actions ({earlier.length})
          </button>
          {open ? earlier.map((a) => <ActionCard key={a.ID} a={a} onUnblock={unblock} />) : null}
        </>
      ) : null}
    </div>
  )
}

/** Un rand al benzii: ce actiune, cine a trimis-o si unde a ajuns (pas cu pas). */
function ActionCard({ a, onUnblock }: { a: IncidentAction; onUnblock?: (a: IncidentAction) => Promise<void> }) {
  return (
    <div className="incident-action" style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
      <strong>{a.label}</strong>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
        <span className="color-green">
          <i className="fa fa-check" aria-hidden="true"></i> Sent {a.sent_at}
          {a.sent_by && !a.auto ? ` by ${a.sent_by}` : ''}
        </span>
        <ActionStep a={a} />
      </div>
      {a.auto ? <AutoNote a={a} onUnblock={onUnblock} /> : null}
      {a.detection_title ? <div className="font-grey-cascade" style={{ fontSize: 12 }}>From detection: {a.detection_title}</div> : null}
      {a.detail ? <pre style={{ fontSize: 11, margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{a.detail}</pre> : null}
    </div>
  )
}

/** Blocarea automata AbuseIPDB: scorul, cand se ridica singura, si "Unblock now" cat timp e activa. */
function AutoNote({ a, onUnblock }: { a: IncidentAction; onUnblock?: (a: IncidentAction) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const auto = a.auto!
  const score = auto.score !== null ? ` ${auto.score}%` : ''
  const text =
    a.action === 'unblock_ip'
      ? 'Automatic · AbuseIPDB block lifted after 24 hours'
      : `Automatic · AbuseIPDB${score}` +
        (auto.unblock_due ? ` · Unblocks automatically at ${auto.unblock_due}` : auto.unblocked_at ? ` · Unblocked ${auto.unblocked_at}` : '')
  const active = a.action === 'block_ip' && auto.unblock_due !== null

  return (
    <div className="incident-action-auto" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, marginTop: 2 }}>
      <span>
        <i className="fas fa-robot" aria-hidden="true"></i> {text}
      </span>
      {active && onUnblock ? (
        <button
          type="button"
          className="btn btn-primary btn-xs incident-action-unblock"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            void onUnblock(a).finally(() => setBusy(false))
          }}
        >
          Unblock now
        </button>
      ) : null}
    </div>
  )
}

function ActionStep({ a }: { a: IncidentAction }) {
  const picked = a.picked_up ? (
    <span className="color-green">
      <i className="fa fa-check" aria-hidden="true"></i> Picked up by the agent
    </span>
  ) : null

  switch (a.result) {
    case 'waiting':
      return (
        <span className="font-grey-cascade">
          <i className="fa fa-hourglass-half" aria-hidden="true"></i> Waiting for the device to pick it up
        </span>
      )
    case 'running':
      return (
        <>
          {picked}
          <span className="font-grey-cascade">
            <i className="fa fa-spinner" aria-hidden="true"></i> Running, no answer yet
          </span>
        </>
      )
    case 'applied':
      return (
        <>
          {picked}
          <span className="color-green">
            <i className="fa fa-check-circle" aria-hidden="true"></i> Applied {a.result_at ?? ''}
          </span>
        </>
      )
    case 'no_answer':
      return (
        <>
          {picked}
          <span className="color-orange">
            <i className="fa fa-question-circle" aria-hidden="true"></i> No answer from the device
          </span>
        </>
      )
    case 'expired':
      return (
        <span className="color-red">
          <i className="fa fa-clock" aria-hidden="true"></i> Expired {a.result_at ?? ''}
        </span>
      )
    default:
      return (
        <>
          {picked}
          <span className="color-red">
            <i className="fa fa-times-circle" aria-hidden="true"></i> {a.result === 'rejected' ? 'Rejected' : 'Failed'} {a.result_at ?? ''}
          </span>
        </>
      )
  }
}
